import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, Avatar, Button, Skeleton, Divider, Paper } from "@mui/material";
import { Building, MapPin, Users, Target, CheckCircle2, ChevronRight, Share2, Rocket, MessageCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../../../../context/AuthContext";

const COMMUNITY_API = "https://apiweb.hitpoly.com/ajax/communityWidgetsController.php";
const BOLSA_API = "https://apiweb.hitpoly.com/ajax/bolsaController.php";

// Cache
const COMPANY_CACHE = {};

const CompanyHoverCard = ({ company, anchorEl, onClose, currentUserId: propUserId, onNavigateCompany, onMouseEnter, onMouseLeave }) => {
  const { user: loggedInUser } = useAuth();
  const currentUserId = propUserId || loggedInUser?.user_id || loggedInUser?.id;
  const companyId = company?.id;
  const [profile, setProfile] = useState(company || null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!companyId || !anchorEl) return;

    if (COMPANY_CACHE[companyId]) {
      setProfile(COMPANY_CACHE[companyId]);
      setIsFollowing(COMPANY_CACHE[companyId].is_following || false);
      setFollowerCount(COMPANY_CACHE[companyId].follower_count || 0);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios.post(COMMUNITY_API, {
      action: "getCompanyMiniProfile",
      company_id: companyId,
      current_user_id: currentUserId
    })
      .then(res => {
        if (res.data?.success && res.data.data) {
          const fullProfile = { ...company, ...res.data.data };
          COMPANY_CACHE[companyId] = fullProfile;
          setProfile(fullProfile);
          setIsFollowing(fullProfile.is_following);
          setFollowerCount(fullProfile.follower_count);
        }
      })
      .catch((err) => {})
      .finally(() => setLoading(false));
  }, [companyId, anchorEl, currentUserId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const toggleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUserId) {
      return;
    }
    
    try {
      const res = await axios.post(BOLSA_API, {
        accion: "toggleFollowCompany",
        empresa_id: companyId,
        user_id: currentUserId
      });
      
      if (res.data?.success) {
        const followed = res.data.action === 'followed';
        setIsFollowing(followed);
        setFollowerCount(prev => followed ? prev + 1 : prev - 1);
        if (COMPANY_CACHE[companyId]) {
          COMPANY_CACHE[companyId].is_following = followed;
          COMPANY_CACHE[companyId].follower_count += followed ? 1 : -1;
        }
      } else {
        console.error("[CompanyHoverCard] Error desde el backend:", res.data?.error);
      }
    } catch (e) {
      console.error("[CompanyHoverCard] Excepción en toggleFollow:", e);
    }
  };

  if (!anchorEl) return null;

  // Calcular posición al costado
  const rect = anchorEl.getBoundingClientRect();
  const popupWidth = 320;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const maxPossibleHeight = Math.max(spaceBelow, spaceAbove, 200) - 10;
  const cardHeight = Math.min(400, maxPossibleHeight);

  // Por defecto, a la derecha
  let left = rect.right + 12;
  let top;

  // Si hay más espacio debajo que arriba, alinear con el borde superior
  if (spaceBelow >= cardHeight) {
    top = rect.top;
  } else if (spaceAbove >= cardHeight) {
    // Si hay espacio arriba, posicionar desde el borde inferior hacia arriba
    top = rect.bottom - cardHeight;
  } else {
    // En caso extremo, centrar verticalmente o pegar al tope
    top = Math.max(10, Math.min(rect.top, window.innerHeight - cardHeight - 10));
  }

  // Asegurar que no se salga del viewport
  if (top + cardHeight > window.innerHeight - 10) {
    top = window.innerHeight - cardHeight - 10;
  }
  if (top < 10) top = 10;

  // Si no cabe a la derecha, lo ponemos a la izquierda
  if (left + popupWidth > window.innerWidth - 10) {
    left = rect.left - popupWidth - 12;
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={24} />
              <Skeleton variant="text" width="60%" />
            </Box>
          </Box>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 0 }}>
        {profile?.banner ? (
          <Box sx={{ height: 80, backgroundImage: `url(${profile.banner})`, backgroundSize: 'cover', backgroundPosition: 'center', borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
        ) : (
          <Box sx={{ height: 60, bgcolor: '#f3f2ef', borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
        )}

        <Box sx={{ px: 2.5, pb: 2.5, mt: profile?.banner ? -4 : -3, position: 'relative' }}>
          <Avatar 
            src={profile?.logo} 
            variant="rounded"
            sx={{ width: 72, height: 72, border: '4px solid white', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          />
          
          <Typography variant="h6" fontWeight="800" sx={{ mt: 1, lineHeight: 1.2, color: '#1a1a1a' }}>
            {profile?.name}
          </Typography>
          <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            {profile?.industry || 'Empresa activa'}
          </Typography>

          {profile?.descripcion && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {profile.descripcion}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {(profile?.ciudad || profile?.tamano) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#666', fontSize: '0.8rem' }}>
                <MapPin size={14} /> 
                <Typography variant="caption" fontWeight="500">{[profile.ciudad, profile.tamano].filter(Boolean).join(' • ')}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#666', fontSize: '0.8rem' }}>
              <Users size={14} /> 
              <Typography variant="caption" fontWeight="500">{followerCount} seguidores</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button
              variant={isFollowing ? "outlined" : "contained"}
              size="small"
              startIcon={isFollowing ? <CheckCircle2 size={14} /> : <Rocket size={14} />}
              onClick={toggleFollow}
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem',
                py: 0.8,
                boxShadow: isFollowing ? 'none' : '0 2px 8px rgba(10,102,194,0.3)',
                bgcolor: isFollowing ? 'transparent' : '#0a66c2',
                color: isFollowing ? '#0a66c2' : '#fff',
                borderColor: isFollowing ? '#0a66c2' : 'transparent',
                '&:hover': { 
                  bgcolor: isFollowing ? '#f3f2ef' : '#004182', 
                  borderColor: isFollowing ? '#004182' : 'transparent',
                  color: isFollowing ? '#004182' : '#fff'
                }
              }}
            >
              {isFollowing ? 'Siguiendo' : 'Seguir'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MessageCircle size={14} />}
              onClick={(e) => { 
                e.stopPropagation(); 
                onClose?.();
                if (profile?.owner_id) {
                  window.postMessage({
                    type: 'OPEN_FLOATING_CHAT',
                    partner: {
                      id: profile.owner_id,
                      name: profile.name,
                      avatar: profile.logo || '',
                      type: 'enterprise'
                    }
                  }, '*');
                } else {
                  console.warn("[CompanyHoverCard] No hay owner_id para esta empresa. No se puede abrir chat.");
                }
              }}
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.8rem',
                color: '#666',
                borderColor: '#ccc',
                '&:hover': { bgcolor: '#f3f2ef', borderColor: '#999' }
              }}
            >
              Mensaje
            </Button>
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={(e) => { e.stopPropagation(); onNavigateCompany?.(companyId); onClose(); }}
            sx={{
              width: '100%',
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 700,
              fontSize: '0.8rem',
              color: '#0a66c2',
              '&:hover': { bgcolor: '#eef3f8' }
            }}
          >
            Ver perfil de empresa
          </Button>
        </Box>
      </Box>
    );
  };

  return ReactDOM.createPortal(
    <Paper
      ref={cardRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      elevation={8}
      sx={{
        position: 'fixed',
        left: left,
        top: top,
        width: popupWidth,
        maxHeight: cardHeight,
        borderRadius: 4,
        zIndex: 999999,
        overflowY: 'auto',
        animation: 'fadeInUp 0.2s ease-out forwards',
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
      onMouseLeave={onClose}
    >
      {renderContent()}
    </Paper>,
    document.body
  );
};

export default CompanyHoverCard;
